import api from './api';

/**
 * Server-side call recording (LiveKit egress → private bucket). Contract:
 * docs/superpowers/specs/2026-10-26-call-recording-design.md, extended by
 * 2026-10-27-recording-management-design.md (per-person modes, managers,
 * Recently deleted, keep forever).
 *
 * Recording state never rides the call snapshot (its zod schema is strict and
 * deployed clients would drop the frame); it has this REST surface and its own
 * `call_recording` WS frame. None of these GETs may be served from the offline
 * cache (see utils/cache/policy.ts): a stale status would show a REC badge on
 * a call that is not recording, and a replayed signed URL is expired.
 */

export type RecordingStatus =
  | 'pending'      // eligible; waiting for two participants to join
  | 'starting'     // egress being started
  | 'recording'    // live
  | 'processing'   // stopped, finalizing upload
  | 'ready'        // playable
  | 'failed'
  | 'unavailable'  // reason: client_update_required | disabled | storage_not_configured
  | 'not_started'  // call ended before two people joined
  | 'deleted';

/** How a person's calls are recorded: by themselves (`auto`), when someone on
 *  the call presses Start recording (`manual`, "During call"), or never (`off`). */
export type RecordingMode = 'auto' | 'manual' | 'off';

export const RECORDING_MODE_LABELS: Record<RecordingMode, string> = {
  auto: 'Auto',
  manual: 'During call',
  off: 'Off',
};

export interface CallRecordingOut {
  id: string;
  call_id: string;
  conversation_id: string | null;
  /** Shared name; null (or absent from an older API) = name it after the people on the call. */
  title?: string | null;
  status: RecordingStatus;
  reason: string | null;
  started_at: string | null;
  ended_at: string | null;
  duration_s: number | null;
  size_bytes: number | null;
  mime_type: string | null;
  /** Viewer-relative. */
  can_download: boolean;
  created_at: string | null;
  // The fields below are absent from an API that predates recording
  // management; read them as "auto, nothing extra allowed".
  /** `manual`: nothing records until a participant presses Start recording. */
  start_mode?: 'auto' | 'manual';
  started_by?: string | null;
  /** Retention never removes it, and nobody can delete it until it is un-kept. */
  keep_forever?: boolean;
  /** Set while it sits in Recently deleted. */
  trashed_at?: string | null;
  /** Viewer-relative: may press Start recording now. */
  can_start?: boolean;
  /** Viewer-relative: may move it to Recently deleted. */
  can_delete?: boolean;
  /** Viewer-relative (admins): may turn keep forever on or off. */
  can_keep?: boolean;
}

export interface RecordingParticipant {
  user_id: string;
  name: string | null;
  avatar_url: string | null;
}

/** A row of `listRecordings`: the recording plus who was on the call. */
export interface RecordingListItem extends CallRecordingOut {
  initiated_by: string | null;
  participants: RecordingParticipant[];
  // Only on `view=trash` rows.
  trashed_by?: string | null;
  trashed_by_name?: string | null;
  /** When it is removed for good. */
  purge_at?: string | null;
}

export interface RecordingPolicyOut {
  enabled: boolean;
  /** 0 = keep forever. */
  retention_days: number;
  allow_download: boolean;
  storage_configured: boolean;
  /** Viewer-relative: true only for organisation admins. */
  can_manage: boolean;
  version: number;
  updated_at: string | null;
  updated_by_name: string | null;
  // Absent from an API that predates recording management.
  /** Mode of anyone without their own setting. */
  default_mode?: RecordingMode;
  participants_can_delete?: boolean;
  /** Reporting managers may delete their direct reports' recordings. */
  managers_can_delete?: boolean;
  /** Days a deleted recording stays in Recently deleted (1–365). */
  trash_days?: number;
  /** Viewer-relative: the viewer's own effective mode. */
  my_mode?: RecordingMode;
  /** Viewer-relative: has direct reports, so may set their modes. */
  is_manager?: boolean;
}

export interface RecordingPolicyUpdate {
  enabled: boolean;
  retention_days: number;
  allow_download: boolean;
  // Omitted = unchanged.
  default_mode?: RecordingMode;
  participants_can_delete?: boolean;
  managers_can_delete?: boolean;
  trash_days?: number;
  /** Optimistic concurrency: a stale version is refused with 409. */
  expected_version: number;
}

export interface RecordingMediaUrl {
  url: string;
  expires_at: string;
  filename: string;
}

/** Statuses during which every participant is told the call is recorded. */
export const RECORDING_NOTICE_STATUSES: ReadonlySet<RecordingStatus> = new Set(['pending', 'starting', 'recording']);

/** Statuses the call history hides entirely. */
export const HIDDEN_RECORDING_STATUSES: ReadonlySet<RecordingStatus> = new Set(['not_started', 'deleted']);

export const getRecordingPolicy = () =>
  api.get<RecordingPolicyOut>('/calls/recording-policy').then(r => r.data);

export const updateRecordingPolicy = (body: RecordingPolicyUpdate) =>
  api.put<RecordingPolicyOut>('/calls/recording-policy', body).then(r => r.data);

/** The call's recording row, or null when the call was not recorded (404). */
export const getCallRecording = (callId: string) =>
  api.get<CallRecordingOut>(`/calls/${encodeURIComponent(callId)}/recording`)
    .then(r => r.data)
    .catch((error: any) => {
      if (error?.response?.status === 404) return null;
      throw error;
    });

/** Manual start ("During call"): 403 when the viewer may not, 409 when the
 *  call is not startable. Pressed before the second person joins, it arms the
 *  recording, which then starts once two have joined. */
export const startCallRecording = (callId: string) =>
  api.post<CallRecordingOut>(`/calls/${encodeURIComponent(callId)}/recording/start`).then(r => r.data);

/** Admins get the whole organisation's recordings, managers their own calls'
 *  plus their direct reports', everyone else only their own calls'. `userId`
 *  narrows to calls that person was on. `view: 'trash'` is Recently deleted:
 *  an admin's is the org's, anyone else's what they deleted and have not
 *  removed from it. */
export const listRecordings = (params?: {
  cursor?: string | null; limit?: number; userId?: string | null; view?: 'all' | 'trash';
}) =>
  api.get<{ items: RecordingListItem[]; next_cursor: string | null }>('/calls/recordings', {
    params: {
      ...(params?.cursor ? { cursor: params.cursor } : {}),
      ...(params?.limit ? { limit: params.limit } : {}),
      ...(params?.userId ? { user_id: params.userId } : {}),
      ...(params?.view ? { view: params.view } : {}),
    },
  }).then(r => r.data);

/** A 10-minute signed URL, minted per request. Never store or cache it. */
export const getRecordingMediaUrl = (recordingId: string, download = false) =>
  api.get<RecordingMediaUrl>(`/calls/recordings/${encodeURIComponent(recordingId)}/media`, {
    params: { download },
  }).then(r => r.data);

/** One recording. Also open to members of a chat it was shared into (the chat card). */
export const getRecording = (recordingId: string) =>
  api.get<CallRecordingOut>(`/calls/recordings/${encodeURIComponent(recordingId)}`).then(r => r.data);

/** One shared name per recording; a blank title clears it. */
export const renameRecording = (recordingId: string, title: string) =>
  api.patch<CallRecordingOut>(`/calls/recordings/${encodeURIComponent(recordingId)}`, { title }).then(r => r.data);

/** Post the recording into a chat as a card; that chat's members may then play it. */
export const shareRecordingToChat = (recordingId: string, conversationId: string) =>
  api.post(`/calls/recordings/${encodeURIComponent(recordingId)}/share`, { conversation_id: conversationId })
    .then(r => r.data);

/** Body of a `call_recording` chat message: a ProseMirror doc (so older
 *  clients render its paragraph) plus the recording it points at. */
export interface RecordingMessageBody {
  recording: { id: string; title: string | null; duration_s: number | null; started_at: string | null };
}

export function recordingMessageBody(body: unknown): RecordingMessageBody['recording'] | null {
  const recording = (body as RecordingMessageBody | null)?.recording;
  return recording && typeof recording.id === 'string' ? recording : null;
}

/** Moves the recording to Recently deleted, for everyone on the call. Allowed
 *  per `can_delete`; refused (409) while in progress, kept forever or on legal hold. */
export const deleteRecording = (recordingId: string) =>
  api.delete(`/calls/recordings/${encodeURIComponent(recordingId)}`).then(() => undefined);

// The contract does not fix what restore / keep return; use a body only when
// it is the recording itself.
const recordingOrNull = (data: unknown): CallRecordingOut | null =>
  data && typeof (data as CallRecordingOut).id === 'string' ? data as CallRecordingOut : null;

/** Back out of Recently deleted. Admins, or whoever deleted it. */
export const restoreRecording = (recordingId: string) =>
  api.post(`/calls/recordings/${encodeURIComponent(recordingId)}/restore`).then(r => recordingOrNull(r.data));

/** An admin removes the file now. Anyone else only removes it from their own
 *  Recently deleted; admins can still restore it until its `purge_at`. */
export const deleteRecordingPermanently = (recordingId: string) =>
  api.delete(`/calls/recordings/${encodeURIComponent(recordingId)}/permanent`).then(() => undefined);

/** Admins only. A kept recording is never removed by retention or deleted. */
export const keepRecording = (recordingId: string, keep: boolean) =>
  api.post(`/calls/recordings/${encodeURIComponent(recordingId)}/keep`, { keep }).then(r => recordingOrNull(r.data));

export interface RecordingMember {
  user_id: string;
  name: string | null;
  avatar_url: string | null;
  /** Effective mode: their own setting, or the org default. */
  mode: RecordingMode;
  /** False = follows the org default. */
  custom: boolean;
}

export interface RecordingMembersOut {
  items: RecordingMember[];
  default_mode: RecordingMode;
  /** `org`: an admin sees everyone; `team`: a manager sees their direct reports. */
  scope: 'org' | 'team';
}

/** Admins: every active person in the org. Managers: their direct reports. Others 403. */
export const listRecordingMembers = () =>
  api.get<RecordingMembersOut>('/calls/recording-members').then(r => r.data);

/** `null` puts the person back on the org default. Admins, or their manager. */
export const setRecordingMemberMode = (userId: string, mode: RecordingMode | null) =>
  api.put(`/calls/recording-members/${encodeURIComponent(userId)}`, { mode }).then(() => undefined);
